"use client";

import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const headlineWords = [{
    text: "Secure",
    italic: false,
    accent: false
  }, {
    text: "your",
    italic: false,
    accent: false
  }, {
    text: "digital",
    italic: true,
    accent: false
  }, {
    text: "footprint",
    italic: false,
    accent: false
  }, {
    text: "with",
    italic: false,
    accent: false
  }, {
    text: "the",
    italic: false,
    accent: false
  }, {
    text: "most",
    italic: false,
    accent: false
  }, {
    text: "trusted",
    italic: true,
    accent: true
  }, {
    text: "VPN",
    italic: false,
    accent: false
  }, {
    text: "solutions.",
    italic: false,
    accent: false
  }];
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: paragraphRef,
    isVisible: paragraphVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [headlineMounted, setHeadlineMounted] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setHeadlineMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const parallaxOffset = Math.min(scrollY * 0.15, 40);
  return <section data-section-id="2778" id="herocomplianceprotection" className="bg-background text-foreground relative overflow-hidden min-h-[110vh] flex items-center">
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-x-4 md:gap-x-6 gap-y-12 md:gap-y-16">
          {}
          <div ref={labelRef} className={`col-span-12 md:col-span-2 transition-all duration-1000 ease-out ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-primary">
              § 00 — security
            </p>
          </div>

          {}
          <div className="col-span-12 md:col-span-9 md:col-start-2">
            <h1 className="font-serif font-light text-foreground" style={{
            fontSize: "clamp(48px, 11vw, 168px)",
            lineHeight: "0.92",
            letterSpacing: "-0.02em"
          }}>
              {headlineWords.map((word, i) => <span data-index={i} key={i} className="inline-block overflow-hidden align-bottom mr-[0.18em]">
                  <span className={`inline-block transition-transform ease-out ${word.italic ? "italic" : ""}`} style={{
                transform: headlineMounted ? "translateY(0%)" : "translateY(110%)",
                transitionDuration: "700ms",
                transitionDelay: `${i * 60}ms`,
                color: word.accent ? "var(--color-primary)" : undefined
              }}>
                    {word.text}
                  </span>
                </span>)}
            </h1>
          </div>

          {}
          <div ref={paragraphRef} className={`col-span-12 md:col-span-4 md:col-start-2 transition-all duration-1000 ease-out delay-300 ${paragraphVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          maxWidth: "320px"
        }}>
            <p className="font-serif italic text-[17px] leading-relaxed text-muted-foreground mb-4">
              Advanced encryption protocols designed for total privacy —
            </p>
            <p className="font-sans text-sm leading-relaxed text-muted-foreground mb-8">
              from seamless browsing to enterprise-grade security, we protect your data across every connection.
            </p>
            <Link to="https://vpngenix.com/go/NordVPN" newTab>
              <Button size="lg" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-7 h-12 font-sans text-sm tracking-wide shadow-md">
                Get Protected
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {}
          <div ref={imageRef} className={`col-span-12 md:col-span-4 md:col-start-9 transition-all duration-1000 ease-out delay-500 ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transform: imageVisible ? `translateY(${-parallaxOffset}px)` : "translateY(2rem)"
        }}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[3/4] bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpngenix_secure_connection.png" alt="Digital shield icon representing secure VPN connection and data privacy" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <div className="absolute inset-0 pointer-events-none" style={{
              background: "radial-gradient(ellipse at center, transparent 50%, hsla(221, 40%, 4%, 0.45) 100%)"
            }} />
            </div>
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mt-4">
              vpngenix / secure / 2024
            </p>
          </div>
        </div>
      </div>

      {}
      <div aria-hidden="true" className="absolute left-0 right-0 bottom-0 h-px bg-border" />
    </section>;
}